<template>
    <kiosk-migration-layout :migration-id="props.migrationId">
        <div
            class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-md) justify-center gap-2 md:gap-4"
        >
            <page-header>Config</page-header>

            <vx-alert v-if="!isDraft" color="warning" class="mb-4">
                Config can only be edited for draft migrations. Current status: {{ migration.status }}
            </vx-alert>

            <page-loading v-if="loading"></page-loading>

            <div v-else class="w-full">
                <vx-box
                    :class="{
                        'rounded-[16px] border border-solid border-slate-300 bg-slate-200': !mappingsOpen,
                    }"
                    :color="mappingsOpen ? 'info' : 'white'"
                    class="py-4 lg:py-6 mb-6"
                >
                    <div class="flex items-center justify-between text-slate-800 px-4 lg:pl-9 lg:pr-6">
                        <div class="flex items-center gap-4">
                            <font-awesome-icon widthAuto :icon="faBarsFilter" class="text-base ml-2" />
                            <span class="font-extrabold lg:text-xl">Source Team Mappings</span>
                            <vx-badge color="primary" size="xs" data-test="mappings-count">
                                {{ mappingsCount }}
                            </vx-badge>
                        </div>
                        <div>
                            <vx-button
                                @click="mappingsOpen = !mappingsOpen"
                                class="w-12 px-4 ml-4"
                                color="muted-dark"
                                size="md"
                                type="button"
                                data-test="toggle-mappings"
                            >
                                <font-awesome-icon
                                    widthAuto
                                    :icon="mappingsOpen ? faCaretUp : faCaretDown"
                                ></font-awesome-icon>
                            </vx-button>
                        </div>
                    </div>

                    <animate-height :height="mappingsOpen ? 'auto' : 0">
                        <div class="mt-4">
                            <div class="relative px-4 lg:px-6">
                                <div class="absolute inset-s-8 inset-bs-0 w-px h-full bg-slate-300 lg:inset-s-16"></div>
                                <template v-for="(mapping, index) in mappings" :key="index">
                                    <div class="relative mb-4 flex flex-col gap-2">
                                        <vx-box
                                            class="relative flex p-4 gap-2 items-start flex-wrap lg:flex-nowrap lg:py-3 lg:pl-6 lg:pr-3"
                                        >
                                            <div class="grow w-full flex flex-col gap-2">
                                                <div class="flex flex-col gap-2 lg:gap-4 lg:grid lg:grid-cols-12">
                                                    <div class="col-span-12 lg:col-span-5">
                                                        <vx-selectable
                                                            v-model="mapping.sourceTeam"
                                                            data-test="mapping-source-team"
                                                            placeholder="Search"
                                                            :loading="loadingTeams"
                                                            :options="availableTeamOptions(mapping)"
                                                            :getOptionKey="(option) => option.value"
                                                            :filterBy="filterTeamOption"
                                                            @search="searchTeams"
                                                            :disabled="!isDraft"
                                                            :clearable="false"
                                                        >
                                                            <template v-slot:option="{ option }">
                                                                <div class="line-clamp-3 break-all min-w-0">
                                                                    {{ option.label }}
                                                                </div>
                                                            </template>
                                                            <template v-slot:selected-option="{ option }">
                                                                <div class="flex items-center">
                                                                    <span class="truncate-left"
                                                                        ><span>{{ option.label }}</span></span
                                                                    >
                                                                </div>
                                                            </template>
                                                        </vx-selectable>
                                                    </div>

                                                    <div
                                                        class="hidden lg:flex lg:col-span-2 items-center justify-center"
                                                        data-test="mapping-arrow"
                                                    >
                                                        <font-awesome-icon
                                                            widthAuto
                                                            :icon="faArrowRight"
                                                            class="text-slate-400"
                                                        />
                                                    </div>

                                                    <div class="col-span-12 lg:col-span-5">
                                                        <vx-selectable
                                                            v-model="mapping.targetGroup"
                                                            :options="availableGroupOptions(mapping)"
                                                            :disabled="!isDraft"
                                                            :loading="loadingSearchGroups"
                                                            placeholder="Search or enter group ID"
                                                            :clearable="false"
                                                            :taggable="true"
                                                            :getOptionKey="(option) => option.value"
                                                            :filterBy="filterGroupOption"
                                                            :createOption="createGroupOption"
                                                            @search="searchGroups"
                                                            data-test="mapping-target-group"
                                                        >
                                                            <template v-slot:option="{ option }">
                                                                <div class="flex items-center gap-2">
                                                                    <font-awesome-icon
                                                                        widthAuto
                                                                        :icon="
                                                                            option.isCorporate ? faCrown : faGrid2Plus
                                                                        "
                                                                        :class="{
                                                                            'text-amber-500': option.isCorporate,
                                                                            'text-red-500':
                                                                                !option.isCorporate && option.isUnknown,
                                                                            'text-slate-500':
                                                                                !option.isCorporate &&
                                                                                !option.isUnknown,
                                                                        }"
                                                                    ></font-awesome-icon>
                                                                    <div class="line-clamp-3 break-all min-w-0">
                                                                        {{ option.label }}
                                                                    </div>
                                                                </div>
                                                            </template>
                                                            <template v-slot:selected-option="{ option }">
                                                                <div class="flex items-center gap-2">
                                                                    <font-awesome-icon
                                                                        widthAuto
                                                                        :icon="
                                                                            option.isCorporate ? faCrown : faGrid2Plus
                                                                        "
                                                                        :class="{
                                                                            'text-amber-500': option.isCorporate,
                                                                            'text-red-500':
                                                                                !option.isCorporate && option.isUnknown,
                                                                            'text-slate-500':
                                                                                !option.isCorporate &&
                                                                                !option.isUnknown,
                                                                        }"
                                                                    ></font-awesome-icon>
                                                                    <span class="truncate-left"
                                                                        ><span>{{ option.label }}</span></span
                                                                    >
                                                                </div>
                                                            </template>
                                                        </vx-selectable>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="flex ml-auto">
                                                <vx-button
                                                    @click="removeMapping(index)"
                                                    type="button"
                                                    size="md"
                                                    color="muted-light"
                                                    :disabled="!isDraft"
                                                    data-test="remove-mapping"
                                                >
                                                    <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                                                </vx-button>
                                            </div>
                                        </vx-box>
                                    </div>
                                </template>

                                <div v-if="isDraft" class="flex items-center gap-4">
                                    <vx-box class="relative p-3 inline-flex space-x-2">
                                        <vx-button
                                            @click="addMapping"
                                            type="button"
                                            size="lg"
                                            color="primary"
                                            data-test="add-mapping-button"
                                        >
                                            Add Mapping
                                            <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                                        </vx-button>
                                    </vx-box>
                                </div>
                            </div>
                        </div>
                    </animate-height>
                </vx-box>

                <vx-box
                    :class="{
                        'rounded-[16px] border border-solid border-slate-300 bg-slate-200': !attributeMappingsOpen,
                    }"
                    :color="attributeMappingsOpen ? 'info' : 'white'"
                    class="py-4 lg:py-6 mb-6"
                >
                    <div class="flex items-center justify-between text-slate-800 px-4 lg:pl-9 lg:pr-6">
                        <div class="flex items-center gap-4">
                            <font-awesome-icon widthAuto :icon="faArrowRightArrowLeft" class="text-base ml-2" />
                            <span class="font-extrabold lg:text-xl">Custom Attribute Mappings</span>
                            <vx-badge color="primary" size="xs" data-test="attribute-mappings-count">
                                {{ attributeMappingsCount }}
                            </vx-badge>
                        </div>
                        <div>
                            <vx-button
                                @click="attributeMappingsOpen = !attributeMappingsOpen"
                                class="w-12 px-4 ml-4"
                                color="muted-dark"
                                size="md"
                                type="button"
                                data-test="toggle-attribute-mappings"
                            >
                                <font-awesome-icon
                                    widthAuto
                                    :icon="attributeMappingsOpen ? faCaretUp : faCaretDown"
                                ></font-awesome-icon>
                            </vx-button>
                        </div>
                    </div>

                    <animate-height :height="attributeMappingsOpen ? 'auto' : 0">
                        <div class="mt-4">
                            <div class="relative px-4 lg:px-6">
                                <div class="absolute inset-s-8 inset-bs-0 w-px h-full bg-slate-300 lg:inset-s-16"></div>
                                <template v-for="(mapping, index) in attributeMappings" :key="index">
                                    <div class="relative mb-4 flex flex-col gap-2">
                                        <vx-box
                                            class="relative flex p-4 gap-2 items-start flex-wrap lg:flex-nowrap lg:py-3 lg:pl-6 lg:pr-3"
                                        >
                                            <div class="grow w-full flex flex-col gap-2">
                                                <div class="flex flex-col gap-2 lg:gap-4 lg:grid lg:grid-cols-12">
                                                    <div class="col-span-12 lg:col-span-5">
                                                        <vx-input
                                                            v-model="mapping.customAttributeKey"
                                                            data-test="mapping-custom-attribute-key"
                                                            placeholder="Custom attribute key"
                                                            :disabled="!isDraft"
                                                        />
                                                    </div>

                                                    <div
                                                        class="hidden lg:flex lg:col-span-2 items-center justify-center"
                                                    >
                                                        <font-awesome-icon
                                                            widthAuto
                                                            :icon="faArrowRight"
                                                            class="text-slate-400"
                                                        />
                                                    </div>

                                                    <div class="col-span-12 lg:col-span-5">
                                                        <vx-selectable
                                                            v-model="mapping.traceableAttribute"
                                                            :options="availableTraceableOptions(mapping)"
                                                            :disabled="!isDraft"
                                                            placeholder="Select traceable attribute"
                                                            :clearable="false"
                                                            :reduce="(option) => option.value"
                                                            :getOptionKey="(option) => option.value"
                                                            data-test="mapping-traceable-attribute"
                                                        >
                                                            <template v-slot:option="{ option }">
                                                                {{ option.label }}
                                                            </template>
                                                            <template v-slot:selected-option="{ option }">
                                                                {{ option.label }}
                                                            </template>
                                                        </vx-selectable>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="flex ml-auto">
                                                <vx-button
                                                    @click="removeAttributeMapping(index)"
                                                    type="button"
                                                    size="md"
                                                    color="muted-light"
                                                    :disabled="!isDraft"
                                                    data-test="remove-attribute-mapping"
                                                >
                                                    <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                                                </vx-button>
                                            </div>
                                        </vx-box>
                                    </div>
                                </template>

                                <div v-if="isDraft" class="flex items-center gap-4">
                                    <vx-box class="relative p-3 inline-flex space-x-2">
                                        <vx-button
                                            @click="addAttributeMapping"
                                            type="button"
                                            size="lg"
                                            color="primary"
                                            :disabled="attributeMappings.length >= 3"
                                            data-test="add-attribute-mapping-button"
                                        >
                                            Add Mapping
                                            <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                                        </vx-button>
                                    </vx-box>
                                </div>
                            </div>
                        </div>
                    </animate-height>
                </vx-box>
            </div>

            <vx-sticky-footer v-if="isDraft" class="flex-wrap max-w-(--breakpoint-sm) mx-auto">
                <vx-button
                    :href="`/kiosk/migrations/${props.migrationId}`"
                    class="order-3 w-full lg:w-20 lg:order-1"
                    size="lg"
                    color="muted-dark"
                    outline
                    data-test="cancel-button"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="saveConfig"
                    data-test="update-config-button"
                    :loading="processing"
                    :disabled="!hasChanges || hasIncompleteMappings || hasIncompleteAttributeMappings"
                    type="button"
                    class="order-1 w-full lg:w-auto lg:grow lg:order-3"
                    size="lg"
                    color="primary"
                >
                    Update Config
                </vx-button>
            </vx-sticky-footer>
        </div>
    </kiosk-migration-layout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { VxAlert, VxBadge, VxBox, VxButton, VxInput, VxSelectable, VxStickyFooter } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faArrowRight,
    faArrowRightArrowLeft,
    faBarsFilter,
    faCaretDown,
    faCaretUp,
    faCirclePlus,
    faCrown,
    faGrid2Plus,
    faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import AnimateHeight from 'vue-animate-height';
import KioskMigrationLayout from './KioskMigrationLayout.vue';
import { useMigrationDetails } from '../composables/useMigrationDetails';
import { adminTeamMigrationsClient, adminTeamsClient, groupsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';

const props = defineProps({
    migrationId: {
        type: Number,
        required: true,
    },
});

const { migration, onMountedMigration } = useMigrationDetails();
const toasted = useToasted();

const loading = ref(true);
const processing = ref(false);
const mappings = ref([]);
const originalConfig = ref({});
const loadingTeams = ref(false);
const mappingsOpen = ref(true);

const teamCache = ref(new Map());
const groupCache = ref(new Map());
const loadingSearchGroups = ref(false);

const attributeMappings = ref([]);
const originalAttributeMappings = ref({});
const attributeMappingsOpen = ref(true);

const TRACEABLE_ATTRIBUTES = [
    { value: 'entry_channel', label: 'Entry Channel' },
    { value: 'membership_status', label: 'Membership Status' },
    { value: 'lifecycle_status', label: 'Lifecycle Status' },
];

const isDraft = computed(() => migration.value?.status === 'draft');

const attributeMappingsCount = computed(() => attributeMappings.value.length);

const hasIncompleteAttributeMappings = computed(() => {
    return attributeMappings.value.some((m) => '' === m.customAttributeKey || null === m.traceableAttribute);
});

const usedTraceableAttributes = computed(() => {
    return new Set(attributeMappings.value.map((m) => m.traceableAttribute).filter((t) => null !== t));
});

const availableTraceableOptions = (currentMapping) => {
    return TRACEABLE_ATTRIBUTES.filter(
        (attr) => !usedTraceableAttributes.value.has(attr.value) || attr.value === currentMapping.traceableAttribute
    );
};

const hasAttributeMappingChanges = computed(() => {
    const current = {};
    attributeMappings.value.forEach((m) => {
        if ('' !== m.customAttributeKey && null !== m.traceableAttribute) {
            current[m.customAttributeKey] = m.traceableAttribute;
        }
    });
    return JSON.stringify(current) !== JSON.stringify(originalAttributeMappings.value || {});
});

const hasChanges = computed(() => {
    const currentSourceMappings = {};
    mappings.value.forEach((m) => {
        if (m.sourceTeam?.value != null) {
            currentSourceMappings[m.sourceTeam.value] = m.targetGroup?.value ?? null;
        }
    });
    const sourceTeamChanges =
        JSON.stringify(currentSourceMappings) !== JSON.stringify(originalConfig.value.source_team_mappings || {});

    return sourceTeamChanges || hasAttributeMappingChanges.value;
});
const mappingsCount = computed(() => mappings.value.length);
const hasIncompleteMappings = computed(() => {
    return mappings.value.some(
        (m) => !m.sourceTeam || m.sourceTeam.value == null || !m.targetGroup || m.targetGroup.value === undefined
    );
});

const filterTeamOption = (option, label, search) => {
    const nameLower = (option.label ?? '').toLowerCase();
    const searchLower = search.toLowerCase();
    const idString = String(option.value ?? '');

    return nameLower.includes(searchLower) || idString === search;
};

const filterGroupOption = (option, label, search) => {
    const nameLower = (option.label ?? '').toLowerCase();
    const searchLower = search.toLowerCase();
    const idString = String(option.value ?? '');

    return nameLower.includes(searchLower) || idString === search;
};

const availableTeamOptions = (currentMapping) => {
    const excludedIds = [
        migration.value?.target_team_id,
        ...mappings.value.filter((m) => m !== currentMapping && m.sourceTeam).map((m) => m.sourceTeam.value),
    ].filter(Boolean);

    const options = [];
    const addedIds = new Set();

    // ALWAYS include current mapping's selected team first
    if (currentMapping.sourceTeam) {
        options.push(currentMapping.sourceTeam);
        addedIds.add(currentMapping.sourceTeam.value);
    }

    teamCache.value.forEach((team) => {
        if (!addedIds.has(team.value) && !excludedIds.includes(team.value)) {
            options.push(team);
        }
    });

    return options;
};

const availableGroupOptions = (currentMapping) => {
    const options = [];
    const addedValues = new Set();

    const corporateOption = { label: 'Corporate', value: null, isCorporate: true, isUnknown: false };
    options.push(corporateOption);
    addedValues.add(null);

    // ALWAYS include current mapping's selected group
    if (currentMapping.targetGroup && currentMapping.targetGroup.value !== null) {
        options.push(currentMapping.targetGroup);
        addedValues.add(currentMapping.targetGroup.value);
    }

    groupCache.value.forEach((group) => {
        if (!addedValues.has(group.value)) {
            options.push(group);
        }
    });

    return options;
};

const searchTeams = debounce((query) => {
    loadingTeams.value = true;

    adminTeamsClient
        .getTeams({ query: query })
        .then((response) => {
            // Add teams to cache (accumulate, don't replace)
            response.data.data.forEach((team) => {
                teamCache.value.set(team.id, {
                    value: team.id,
                    label: team.name,
                });
            });

            // Trigger Vue reactivity
            teamCache.value = new Map(teamCache.value);
        })
        .catch((e) => {
            console.error(e);
            toasted.error('Failed to load teams');
        })
        .finally(() => {
            loadingTeams.value = false;
        });
}, 300);

const searchGroups = debounce((query) => {
    if (!migration.value?.target_team_id) {
        return;
    }

    loadingSearchGroups.value = true;

    groupsClient
        .getGroups({ teamId: migration.value.target_team_id }, { query: query, setup_required: 0 })
        .then(({ data }) => {
            // Add groups to cache (accumulate, don't replace)
            data.data.forEach((group) => {
                groupCache.value.set(group.group_id, {
                    value: group.group_id,
                    label: group.name,
                    isCorporate: false,
                    isUnknown: false,
                });
            });

            // Trigger Vue reactivity
            groupCache.value = new Map(groupCache.value);
        })
        .catch((e) => {
            console.error(e);
            toasted.error('Failed to load groups');
        })
        .finally(() => {
            loadingSearchGroups.value = false;
        });
}, 300);

const loadTeamsByIds = async (teamIds) => {
    if (teamIds.length === 0) {
        return;
    }

    const missingIds = teamIds.filter((id) => !teamCache.value.has(id));
    if (missingIds.length === 0) {
        return;
    }

    // Batch into groups of 10 (backend limit)
    const batches = [];
    for (let i = 0; i < missingIds.length; i += 10) {
        batches.push(missingIds.slice(i, i + 10));
    }

    const batchPromises = batches.map((batch) =>
        adminTeamsClient
            .getTeams({ ids: batch })
            .then((response) => {
                response.data.data.forEach((team) => {
                    teamCache.value.set(team.id, {
                        value: team.id,
                        label: team.name,
                    });
                });
            })
            .catch((e) => {
                console.error('Failed to load teams by IDs:', batch, e);
            })
    );

    await Promise.all(batchPromises);

    // Trigger Vue reactivity
    teamCache.value = new Map(teamCache.value);
};

const loadGroupsByIds = async (groupIds) => {
    if (groupIds.length === 0 || !migration.value?.target_team_id) {
        return;
    }

    // Null (Corporate) doesn't need loading
    const missingIds = groupIds.filter((id) => id !== null && !groupCache.value.has(id));
    if (missingIds.length === 0) {
        return;
    }

    // Batch into groups of 10 (backend limit)
    const batches = [];
    for (let i = 0; i < missingIds.length; i += 10) {
        batches.push(missingIds.slice(i, i + 10));
    }

    try {
        const batchPromises = batches.map((batch) =>
            groupsClient
                .getGroups({ teamId: migration.value.target_team_id }, { group_ids: batch, setup_required: 0 })
                .then(({ data }) => {
                    data.data.forEach((group) => {
                        groupCache.value.set(group.group_id, {
                            value: group.group_id,
                            label: group.name,
                            isCorporate: false,
                            isUnknown: false,
                        });
                    });
                })
                .catch((e) => {
                    console.error('Failed to load groups by IDs:', batch, e);
                })
        );

        await Promise.all(batchPromises);

        // For any group IDs still missing (deleted groups), create placeholder entries
        missingIds.forEach((groupId) => {
            if (!groupCache.value.has(groupId)) {
                groupCache.value.set(groupId, {
                    value: groupId,
                    label: groupId,
                    isCorporate: false,
                    isUnknown: true,
                });
            }
        });

        // Trigger Vue reactivity
        groupCache.value = new Map(groupCache.value);
    } catch (e) {
        console.error('Failed to load groups:', e);

        // Even on error, create placeholder entries so UI doesn't break
        missingIds.forEach((groupId) => {
            if (!groupCache.value.has(groupId)) {
                groupCache.value.set(groupId, {
                    value: groupId,
                    label: groupId,
                    isCorporate: false,
                    isUnknown: true,
                });
            }
        });

        groupCache.value = new Map(groupCache.value);
    }
};

const createGroupOption = (input) => {
    return {
        label: input,
        value: input,
        isCorporate: false,
        isUnknown: true,
    };
};

const loadConfig = async () => {
    try {
        loading.value = true;

        const configResponse = await adminTeamMigrationsClient.showConfig(props.migrationId);
        originalConfig.value = configResponse.data;
        originalAttributeMappings.value = configResponse.data.attribute_mappings || {};

        const sourceTeamMappings = configResponse.data.source_team_mappings || {};
        const sourceTeamIds = Object.keys(sourceTeamMappings).map((id) => parseInt(id));
        const targetGroupIds = Object.values(sourceTeamMappings);

        // Ensure Corporate option is always in group cache
        groupCache.value.set(null, { label: 'Corporate', value: null, isCorporate: true, isUnknown: false });

        await Promise.all([loadTeamsByIds(sourceTeamIds), loadGroupsByIds(targetGroupIds)]);

        mappings.value = sourceTeamIds.map((sourceTeamId) => {
            const teamOption = teamCache.value.get(sourceTeamId) || {
                value: sourceTeamId,
                label: `${sourceTeamId}`,
            };

            const targetGroupId = sourceTeamMappings[sourceTeamId];
            const groupOption = groupCache.value.get(targetGroupId) || {
                value: targetGroupId,
                label: targetGroupId,
                isCorporate: false,
                isUnknown: true,
            };

            return {
                sourceTeam: teamOption,
                targetGroup: groupOption,
            };
        });

        const attrMappingsData = configResponse.data.attribute_mappings || {};
        attributeMappings.value = Object.entries(attrMappingsData).map(([key, traceable]) => ({
            customAttributeKey: key,
            traceableAttribute: traceable,
        }));

        // Pre-populate caches with initial search (non-debounced, awaited)
        await Promise.all([
            adminTeamsClient
                .getTeams({ query: '' })
                .then((response) => {
                    response.data.data.forEach((team) => {
                        teamCache.value.set(team.id, {
                            value: team.id,
                            label: team.name,
                        });
                    });
                    teamCache.value = new Map(teamCache.value);
                })
                .catch((e) => {
                    console.error('Failed to pre-load teams:', e);
                }),

            migration.value?.target_team_id
                ? groupsClient
                      .getGroups({ teamId: migration.value.target_team_id }, { query: '', setup_required: 0 })
                      .then(({ data }) => {
                          data.data.forEach((group) => {
                              groupCache.value.set(group.group_id, {
                                  value: group.group_id,
                                  label: group.name,
                                  isCorporate: false,
                                  isUnknown: false,
                              });
                          });
                          groupCache.value = new Map(groupCache.value);
                      })
                      .catch((e) => {
                          console.error('Failed to pre-load groups:', e);
                      })
                : Promise.resolve(),
        ]);
    } catch (e) {
        console.error('Failed to load config:', e);
        toasted.error('Failed to load config');
    } finally {
        loading.value = false;
    }
};

const addMapping = () => {
    mappings.value.push({
        sourceTeam: null,
        targetGroup: null,
    });
};

const removeMapping = (index) => {
    mappings.value.splice(index, 1);
};

const addAttributeMapping = () => {
    attributeMappings.value.push({
        customAttributeKey: '',
        traceableAttribute: null,
    });
};

const removeAttributeMapping = (index) => {
    attributeMappings.value.splice(index, 1);
};

const saveConfig = async () => {
    if (!isDraft.value) {
        return;
    }

    try {
        processing.value = true;

        const sourceTeamMappings = {};
        mappings.value
            .filter(
                (m) => m.sourceTeam && m.sourceTeam.value != null && m.targetGroup && m.targetGroup.value !== undefined
            )
            .forEach((m) => {
                sourceTeamMappings[m.sourceTeam.value] = m.targetGroup.value;
            });

        const attrMappings = {};
        attributeMappings.value
            .filter((m) => '' !== m.customAttributeKey && null !== m.traceableAttribute)
            .forEach((m) => {
                attrMappings[m.customAttributeKey.trim().toLowerCase()] = m.traceableAttribute;
            });

        await adminTeamMigrationsClient.updateConfig(props.migrationId, {
            source_team_mappings: sourceTeamMappings,
            attribute_mappings: attrMappings,
        });

        toasted.success('Config saved successfully');
        originalConfig.value = { source_team_mappings: sourceTeamMappings };
        originalAttributeMappings.value = attrMappings;
    } catch (e) {
        console.error('Failed to save config:', e);
        toasted.error(e.response?.data?.message || 'Failed to save config');
    } finally {
        processing.value = false;
    }
};

onMountedMigration(() => {
    loadConfig();
});
</script>
